'use client';

import { useEffect } from 'react';
import { selectUser, useAuthMe } from '@lib/auth/hooks/use-auth-me';
import { APP_THEME_STORAGE_KEY, isThemeId } from '../app-themes';
import { useAppTheme } from '../use-app-theme';

/**
 * Applies the account theme (`users.theme`) after a client-side
 * sign-in. The root layout no longer awaits the account theme;
 * this sync (and the pre-paint inline script for device storage)
 * applies it instead. next-themes reads `defaultTheme` once at
 * mount — so after
 * a soft sign-in (`router.replace` from login) the account
 * theme would otherwise appear only on the next hard reload.
 *
 * An explicit device choice wins over the account value: the
 * sync runs only while `app-theme` localStorage is empty (same
 * precedence as the server-side defaultTheme path). next-themes
 * persists the applied value, so a synced device keeps showing
 * the sign-in-time theme until the user switches again.
 *
 * Renders nothing.
 */
export function ThemeAccountSync() {
  const {
    data: user
  } = useAuthMe(selectUser);
  const {
    mounted,
    theme,
    setThemeLocal
  } = useAppTheme();
  const savedTheme = user?.configurations?.theme;
  useEffect(() => {
    if (!mounted || !savedTheme || !isThemeId(savedTheme)) return;
    if (savedTheme === theme) return;
    if (localStorage.getItem(APP_THEME_STORAGE_KEY)) return;
    setThemeLocal(savedTheme);
  }, [mounted, savedTheme, theme, setThemeLocal]);
  return null;
}